import { Toasts } from '@fourthwall/components'
import { OrderApiClient } from '@fourthwall/services/dist/OrderApiClient'
import { downloadCsvFile } from '@fourthwall/utils/dist/downloadCsvFile'
import { useCurrentShopQuery } from '@modules/Dashboard/hooks/useCurrentShopQuery'
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider'
import { useMutation } from '@tanstack/react-query'

import type { OrderRow } from '../CsvExport.types'
import { formatMoney } from '../utils/formatMoney'
import { getOrderType } from '../utils/getOrderType'
import { sortOrderRowsByKey } from '../utils/sortOrderRowsByKey'

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSSSSZZ'

export const useOrdersCsvExport = () => {
  const { currentShopQuery } = useCurrentShopQuery()
  const { timezoneAbbreviation, formatInShopTime } = useShopTime()
  const { mutate, isLoading } = useMutation({
    mutationFn: async () => {
      const { data } = await OrderApiClient.exportOrders()

      return data
    },
  })

  const dateKey = `DATE (${timezoneAbbreviation})` as const

  const download = () => {
    if (!currentShopQuery.data) return

    mutate(undefined, {
      onSuccess: (data) => {
        // const data = response.data
        const orders = data.orders.map(
          (order) =>
            ({
              // @ts-ignore
              TYPE: getOrderType(order),
              ID: order.id!,
              'FRIENDLY ID': order.friendly_id,
              [dateKey]: formatInShopTime(order.created_at!, DATE_FORMAT),
              STATUS: order.status!,
              'SUPPORTER EMAIL': order.email!,
              'SUPPORTER PHONE': order.supporter_phone_number || '',
              'MARKETING OPT-IN': order.email_marketing_opt_in as boolean,
              MERCHANDISE: formatMoney(order.sales_price, order.sales_price_currency),
              DONATION: formatMoney(order.donation, order.sales_price_currency),
              SHIPPING: formatMoney(order.shipping_price, order.sales_price_currency),
              TAXES: formatMoney(order.tax_price, order.sales_price_currency),
              DISCOUNT: formatMoney(order.discount, order.sales_price_currency),
              REFUNDED: formatMoney(
                order.refunds_aggregate?.aggregate?.sum?.amount,
                order.sales_price_currency,
              ),
              'SHIPPING NAME': [order.shipping_first_name, order.shipping_lastName]
                .join(' ')
                .trim(),
              'SHIPPING ADDRESS': [order.shipping_address1, order.shipping_address2]
                .join(' ')
                .trim(),
              'SHIPPING CITY': order.shipping_city as string,
              'SHIPPING STATE': order.shipping_state as string,
              'SHIPPING ZIP': order.shipping_zip as string,
              'SHIPPING COUNTRY': order.shipping_country as string,
              'SHIPPING PHONE NUMBER': order.shipping_phone_number as string,
              'BILLING FIRST NAME': order.billing_first_name as string,
              'BILLING LAST NAME': order.billing_lastName as string,
              'BILLING ADDRESS': [order.billing_address1, order.billing_address2].join(' ').trim(),
              'BILLING CITY': order.billing_city as string,
              'BILLING STATE': order.billing_state as string,
              'BILLING ZIP': order.billing_zip as string,
              'BILLING COUNTRY': order.billing_country as string,
              USERNAME:
                // @ts-ignore
                order.additional_fields.additionalFields?.find((field) => field.first == 'username')
                  ?.second || '',
              MESSAGE: order.message?.replace(/\s/g, ' ') ?? '',
              'PROMOTION CODE': order?.promotion?.code || '',
              'REFUND REASONS': order.refunds?.map((refund) => refund.reason).join(', '),
              'TAX ID': (order.tax_id as string) || '',
            }) satisfies OrderRow,
        )

        const donations = data.donations.map(
          (donation) =>
            ({
              TYPE: 'Donation',
              ID: donation.id,
              [dateKey]: formatInShopTime(donation.created_at!!, DATE_FORMAT),
              STATUS: donation.status,
              'SUPPORTER EMAIL': donation.email as string,
              'MARKETING OPT-IN': false,
              MERCHANDISE: '',
              DONATION: formatMoney(donation.donation, donation.donation_currency),
              USERNAME: donation?.username || '',
              MESSAGE: donation?.message?.replace(/\s/g, ' ') || '',
            }) satisfies OrderRow,
        )

        const giveaways = data.giveaways.map(
          (giveaway) =>
            ({
              TYPE: 'Twitch gift purchase',
              ID: giveaway.id,
              'FRIENDLY ID': giveaway.friendly_id,
              [dateKey]: formatInShopTime(giveaway.created_at, DATE_FORMAT),
              STATUS: 'COMPLETED',
              'SUPPORTER EMAIL': giveaway.email as string,
              'MARKETING OPT-IN': false,
              MERCHANDISE: formatMoney(giveaway.total_price, giveaway.currency),
              DONATION: formatMoney(giveaway.donation, giveaway.currency),
              DISCOUNT: formatMoney(giveaway.discount, giveaway.currency),
              USERNAME: giveaway?.username || '',
              MESSAGE: giveaway?.message?.replace(/\s/g, ' ') || '',
            }) satisfies OrderRow,
        )

        const sortedData = sortOrderRowsByKey([...orders, ...donations, ...giveaways], dateKey)

        if (!sortedData.length) {
          Toasts.notify("You haven't received any orders to export yet", { type: 'error' })

          return
        }

        downloadCsvFile(sortedData, `${currentShopQuery.data.name}-orders`)
      },
    })
  }

  return {
    ordersList: {
      download,
      isLoading,
    },
  }
}
